<template>
  <div
    :class="{
      'product-detail-head-fi': true,
      'product-detail-head-fi--no-margin-bottom': noMarginBottom,
    }"
  >
    <template v-if="catalogData">
      <grid-container-fi>
        <div class="fi-col-start-1 fi-col-end-13 md:fi-col-end-7">
          <catalog-slider-fi
            :catalog-data="catalogData"
            :item-name="productName"
          />
        </div>
        <div class="fi-col-start-1 fi-col-end-13 md:fi-col-start-7 md:fi-col-end-13">
          <!-- eslint-disable vue/no-v-html -->
          <headline-fi
            v-if="articleNumber"
            class="product-detail-head-fi__art-nr"
            tag="div"
            color="brand"
            :size="24"
          >
            <span
              data-test="article-number"
              v-html="articleNumber"
            />
          </headline-fi>

          <div
            v-if="eanCode"
            class="product-detail-head-fi__ean-code"
          >
            {{ eanCode }}
          </div>

          <headline-fi
            v-if="productName"
            class="product-detail-head-fi__name"
            tag="h1"
            :size="48"
          >
            <span v-html="productName" />
          </headline-fi>
          <!-- eslint-enable vue/no-v-html -->

          <availability-status-view-only-fi
            v-if="hasAvailabilityStatusOnlyPermission && isLG"
            :fields="fields"
            :is-seefelder="isSeefelder"
          />

          <div
            v-else-if="isShop && !hasAvailabilityStatusOnlyPermission"
            id="buy-box-desktop"
            class="product-detail-head-fi__buy-box"
          />

          <div class="product-detail-head-fi__links">
            <link-fi
              v-if="categorySlug"
              class="product-detail-head-fi__link"
              :href="categorySlug"
              :size="18"
              data-test="product-variants-link"
            >
              {{ $dict.get('product-Family.Anchor-Link-Text') }}
            </link-fi>

            <cad-link-fi
              v-if="hasCADLink && fields"
              class="product-detail-head-fi__link"
              :fields="fields"
              :cad-url="cadUrl"
              open-in-dialog
              @open-cad-details-dialog="onOpenCadDetailsDialog"
            />

            <link-fi
              v-if="
                fields.customLink?.value &&
                (fields.customLink.value.href || fields.customLink.value.url)
              "
              class="product-detail-head-fi__link"
              :href="
                fields.customLink.value.href
                  ? fields.customLink.value.href
                  : fields.customLink.value.url
              "
              :external="Boolean(fields.customLink.value.url)"
              :target="fields.customLink.value.target"
              :size="18"
            >
              {{ fields.customLink.value.text }}
            </link-fi>

            <link-fi
              v-if="mediaDownloadLink && fields.mediaDownloadLabel?.value"
              class="product-detail-head-fi__link"
              href="#"
              rel="nofollow"
              :size="18"
              @click.stop.prevent="openMediaDownloadDialog"
            >
              {{ fields.mediaDownloadLabel.value }}
            </link-fi>
          </div>

          <system-partner-hint-fi
            v-if="showHint"
            class="product-detail-head-fi__system-partner-hint"
          />

          <grid-columns-fi :columns="6">
            <div class="fi-col-span-6 lg:fi-col-span-5">
              <div class="product-detail-head-fi__features">
                <text-expander-fi
                  v-if="features"
                  :content="{ value: features }"
                  :max-height="400"
                  :expand-text="$dict.get('global-Labels.Expander.Expand')"
                  :reduce-text="$dict.get('global-Labels.Expander.Reduce')"
                />
              </div>

              <div
                v-if="
                  !hasAvailabilityStatusOnlyPermission &&
                  !isSystemPartnerItem &&
                  (isRetailConnect || isRetailConnectCart)
                "
                class="product-detail-head-fi__retail-connect-cart-actions product-detail-head-fi__retail-connect-cart-actions--md"
              >
                <button-fi
                  v-if="localRetailers"
                  :disabled="!hasLocalRetailers"
                  type="button"
                  class="button-fi__primary--large"
                  data-test="retail-connect-cart-action-button"
                  data-test-2="retail-connect-local-retailers"
                  @click.stop.prevent="openRetailSearchDialog"
                >
                  {{ $dict.get('dealer-Locator.Find-your-local-Dealer') }}
                </button-fi>

                <button-fi
                  v-if="isRetailConnect"
                  type="button"
                  data-test="retail-connect-cart-action-button"
                  data-test-2="retail-connect-retail-connect"
                  :class="[
                    'button__find-retail-connect',
                    localRetailers ? 'button-fi__tertiary--large' : 'button-fi__primary--large',
                  ]"
                  :disabled="!retailers || retailers.length === 0"
                  @click.stop.prevent="openProductRetailerDialog"
                >
                  {{ $dict.get('online-Shop.Product.Retail-Connect.Buy-Product') }}
                </button-fi>

                <button-fi
                  v-if="isRetailConnectCart"
                  class="button-fi__tertiary--large"
                  type="button"
                  data-test="retail-connect-cart-action-button"
                  data-test-2="retail-connect-retail-connect-cart"
                  :disabled="Boolean(retailerNotAvailable || productInCart || addToCartLoading)"
                  @click.stop.prevent="addProduct"
                >
                  {{
                    productInCart
                      ? $dict.get('online-Shop.Product.Already-in-Cart')
                      : $dict.get('online-Shop.Product.Add-to-Cart')
                  }}
                </button-fi>
              </div>
            </div>
          </grid-columns-fi>
        </div>

        <media-download-dialog-fi
          ref="mediaDownloadDialog"
          :media-download-link="mediaDownloadLink"
          :file-name="mediaDownloadFileName"
          :fields="fields"
        />

        <retail-search-dialog-fi
          ref="retailSearchDialog"
          :fields="fields"
          :catalog-id="catalogData.ProductId"
          :product-name="productName"
        />

        <product-retailer-dialog-fi
          ref="productRetailerDialog"
          :retailers="retailers"
          :retailer-facets="retailerFacets"
        />
      </grid-container-fi>

      <availability-status-view-only-fi
        v-if="hasAvailabilityStatusOnlyPermission && !isLG"
        :fields="fields"
        :is-seefelder="isSeefelder"
      />

      <span
        v-intersect="onIntersect"
        class="sticky-actions-intersector"
      />
      <div
        v-if="!hasAvailabilityStatusOnlyPermission"
        :class="{
          'product-detail-head-fi__actions-mobile': true,
          'product-detail-head-fi__actions-mobile--sticky': stickyEnabled,
        }"
      >
        <div
          v-if="isShop"
          class="product-detail-head-fi__buy-box"
        >
          <teleport
            v-if="productDetailHeadMounted"
            to="#buy-box-desktop"
            :disabled="!isMD"
          >
            <buy-box-module
              :is-seefelder="isSeefelder"
              @added-to-memolist="$emit('added-to-memolist')"
            />
          </teleport>
        </div>

        <div
          v-else-if="
            !isSystemPartnerItem && (isRetailConnect || isRetailConnectCart || localRetailers)
          "
          class="product-detail-head-fi__retail-connect-cart-actions"
        >
          <button-fi
            v-if="localRetailers"
            type="button"
            class="button-fi__primary--large button__find-retailer"
            data-test="retail-connect-cart-action-button"
            data-test-2="retail-connect-local-retailers"
            :disabled="!hasLocalRetailers"
            @click.stop.prevent="openRetailSearchDialog"
          >
            {{ $dict.get('dealer-Locator.Find-your-local-Dealer') }}
          </button-fi>

          <button-fi
            v-if="isRetailConnect"
            type="button"
            data-test="retail-connect-cart-action-button"
            data-test-2="retail-connect-retail-connect"
            :class="[
              'button__find-retail-connect',
              localRetailers ? 'button-fi__tertiary--large' : 'button-fi__primary--large',
            ]"
            :disabled="retailers !== null && retailers.length === 0"
            @click.stop.prevent="openProductRetailerDialog"
          >
            {{ $dict.get('online-Shop.Product.Retail-Connect.Buy-Product') }}
          </button-fi>

          <button-fi
            v-if="isRetailConnectCart"
            type="button"
            class="button-fi__tertiary--large button__add-to-cart"
            data-test="retail-connect-cart-action-button"
            data-test-2="retail-connect-retail-connect-cart"
            :disabled="Boolean(retailerNotAvailable || productInCart || addToCartLoading)"
            @click.stop.prevent="addProduct"
          >
            {{
              productInCart
                ? $dict.get('online-Shop.Product.Already-in-Cart')
                : $dict.get('online-Shop.Product.Add-to-Cart')
            }}
          </button-fi>
        </div>
      </div>

      <cad-details-dialog-fi
        ref="cadDetailsDialog"
        :cad-url="cadUrl"
        lazy
      />
    </template>

    <div id="price-row-savings" />
  </div>
</template>

<script lang="ts" setup>
import { useQuery } from '@urql/vue';
import { ButtonFi, HeadlineFi, LinkFi } from 'atoms';
import { computed, defineAsyncComponent, onMounted, ref } from 'vue';

import CadLinkFi from '@/components/cad-link-fi/CadLinkFi.vue';
import CatalogSliderFi from '@/components/catalog-slider-fi/CatalogSliderFi.vue';
import GridColumnsFi from '@/components/grid-fi/GridColumnsFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import CadDetailsDialogFi from '@/components/product-detail/cad-details-dialog-fi/CadDetailsDialogFi.vue';
import MediaDownloadDialogFi from '@/components/product-detail/media-download-dialog-fi/MediaDownloadDialogFi.vue';
import ProductRetailerDialogFi from '@/components/product-detail/product-retailer-dialog-fi/ProductRetailerDialogFi.vue';
import RetailSearchDialogFi from '@/components/product-detail/retail-search-dialog-fi/RetailSearchDialogFi.vue';
import BuyBoxModule from '@/components/shop-modules/buy-box-module/BuyBoxModule.vue';
import SystemPartnerHintFi from '@/components/system-partner-hint-fi/SystemPartnerHintFi.vue';
import TextExpanderFi from '@/components/text-expander-fi/TextExpanderFi.vue';
import { useAccessPrivileges } from '@/composables/accessPrivileges';
import { useBreakpoints } from '@/composables/breakpoint';
import { useCatalogAttributes } from '@/composables/catalogAttributes';
import { useCatalogTextList } from '@/composables/catalogTextList';
import { useFields } from '@/composables/fields';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import { useOnlineRetailers } from '@/composables/onlineRetailers';
import { useSeefelder } from '@/composables/seefelder';
import { useShoppingcart } from '@/composables/shoppingcart';
import { useSystemPartner } from '@/composables/systemPartner';
import GET_RETAILER_AVAILABILITY_FOR_IDS from '@/graphql/getRetailerAvailabilityForIds.gql';
import HAS_LOCAL_RETAILERS from '@/graphql/hasLocalRetailers.gql';
import { useLayoutStore } from '@/store/pinia/layout';
import { useNotificationStore } from '@/store/pinia/notification';

const AvailabilityStatusViewOnlyFi = defineAsyncComponent(
  () =>
    import('@/components/product-detail/product-detail-head-fi/availability-status-view-only-fi/AvailabilityStatusViewOnlyFi.vue'),
);

interface CatalogData {
  ProductId?: string | null;
  ProductName?: string | null;
  RootCategory?: string | null;
  TextList?: any[] | null;
  Attributes?: any[] | null;
  Media?: any[] | null;
  hasCADLink?: boolean | null;
  SystemItem?: boolean | null;
  categoryData?: null | {
    CategoryRole?: string | null;
    Slug?: string | null;
    categoryName?: string | null;
  };
  seriesData?: null | {
    Name?: string | null;
    Slug?: string | null;
    seriesMedia?: string | null;
  };
}

interface Props {
  catalogData?: CatalogData | null;
  mediaDownloadLink?: string | null;
  noMarginBottom?: boolean;
}

interface Emits {
  (e: 'added-to-memolist'): void;
}

const props = withDefaults(defineProps<Props>(), {
  catalogData: null,
  mediaDownloadLink: null,
  noMarginBottom: false,
});

defineEmits<Emits>();

const fischer = useFischer();
const dict = useDict();
const { openGeneralErrorNotification } = useNotificationStore();
const { hasAvailabilityStatusOnlyPermission } = useAccessPrivileges();
const { isMD, isLG } = useBreakpoints();
const { addProductToCart, getCart, cart } = useShoppingcart({
  enterErrorState: (...errors) => {
    console.error(...errors);
    openGeneralErrorNotification();
  },
});
const { textListValue } = useCatalogTextList();
const { attributeValue } = useCatalogAttributes();
const layoutStore = useLayoutStore();

const mediaDownloadDialog = ref<InstanceType<typeof MediaDownloadDialogFi> | null>(null);
const retailSearchDialog = ref<InstanceType<typeof RetailSearchDialogFi> | null>(null);
const productRetailerDialog = ref<InstanceType<typeof ProductRetailerDialogFi> | null>(null);
const cadDetailsDialog = ref<InstanceType<typeof CadDetailsDialogFi> | null>(null);

const addToCartLoading = ref(false);
const stickyEnabled = ref(false);

const fields = useFields('ProductDetailPageFiFields');

const localRetailers = computed(() => fischer.localRetailers());
const isShop = computed(() => fischer.shop.isClosedShop() || fischer.shop.isOnlineShop());
const isRetailConnect = computed(() => fischer.shop.isRetailConnect());
const isRetailConnectCart = computed(() => fischer.shop.isRetailConnectCart());

const { isSystemItem, showSystemPartnerHint } = useSystemPartner();
const isSystemPartnerItem = computed(() => isSystemItem(props.catalogData));
const showHint = computed(() => showSystemPartnerHint(props.catalogData));

const productIds = computed(() => {
  if (props.catalogData?.ProductId) {
    return [props.catalogData.ProductId];
  }
  return null;
});

const productInCart = computed(() => {
  let inCart = false;
  if (cart.value?.products) {
    inCart = cart.value.products.some(
      (product) => product?.articleNo === props.catalogData?.ProductId,
    );
  }
  return inCart;
});

// R1: !IS_SSR guard → pause (inverted); no fetchPolicy → requestPolicy: 'network-only'; NO R0 (both explicitly guard with !IS_SSR)
const { data: hasLocalRetailersData } = useQuery({
  query: HAS_LOCAL_RETAILERS,
  variables: computed(() => ({
    brand: fischer.brandId(),
    productId: props.catalogData?.ProductId || '',
    locale: fischer.catalogLocale(),
  })),
  pause: computed(() => IS_SSR || !localRetailers.value || !props.catalogData?.ProductId),
  requestPolicy: 'network-only',
});

const { data: retailerAvailabilityData } = useQuery({
  query: GET_RETAILER_AVAILABILITY_FOR_IDS,
  variables: computed(() => ({
    baseSiteId: fischer.baseSiteId(),
    productIds: productIds.value || [],
    useGtins: fischer.useGtins(),
  })),
  pause: computed(
    () =>
      IS_SSR || !isRetailConnectCart.value || productInCart.value || !props.catalogData?.ProductId,
  ),
});

const { retailers, retailerFacets } = useOnlineRetailers({
  productIds,
  fischer,
  dict,
  isRetailConnect,
  isRetailConnectCart,
});

const productDetailHeadMounted = computed(() => layoutStore.productDetailHeadMounted);

const hasLocalRetailers = computed(() => hasLocalRetailersData.value?.hasLocalRetailers ?? false);

const getRetailerAvailabilityForIds = computed(
  () => retailerAvailabilityData.value?.getRetailerAvailabilityForIds || [],
);

const retailerNotAvailable = computed(() => {
  const stock = getRetailerAvailabilityForIds.value?.[0]?.stock;
  return stock === 'empty' || stock === null;
});

const { isSeefelder, getIsSeefelder } = useSeefelder(() => props.catalogData);

const articleNumber = computed(() => {
  const productId = props.catalogData?.ProductId;
  if (!productId) return '';
  const isCraftSupply = getIsSeefelder(props.catalogData);
  const artNr = isCraftSupply ? productId : Number(productId);
  return `${dict.get('product-List.Table.Article-Number')} ${artNr}`;
});

const categorySlug = computed(() => {
  if (fischer.isProductSeriesEnabled()) {
    return (
      props.catalogData?.seriesData?.Slug &&
      `${fischer.pages.products().url}${props.catalogData.seriesData.Slug}#productList`
    );
  }

  return (
    props.catalogData?.categoryData?.CategoryRole === 'FAMILY_PAGE' &&
    props.catalogData?.categoryData?.Slug &&
    `${fischer.pages.products().url}${props.catalogData.categoryData.Slug}#productList`
  );
});

const productName = computed(() => {
  return textListValue(props.catalogData?.TextList, 'ECOMLANG');
});

const features = computed(() => {
  return textListValue(props.catalogData?.TextList, 'POCTOPFEATURESGEN');
});

const eanCode = computed(() => {
  const eanCodeValue = attributeValue(props.catalogData?.Attributes, 'EAN_CODE');
  return eanCodeValue ? `${dict.get('product-List.Table.EAN-Code')}: ${eanCodeValue}` : null;
});

const hasCADLink = computed(() => props.catalogData?.hasCADLink);

const cadUrl = computed(() => {
  if (
    !props.catalogData?.ProductId ||
    !fields.cadLink?.value ||
    !fields.cadLanguage?.value ||
    !fields.cadMandant?.value ||
    !fields.cadLinkText?.value
  ) {
    return null;
  }
  return `${fields.cadLink.value}?mandant=${fields.cadMandant.value}&cul=${
    fields.cadLanguage.value
  }&CadBox=2&ArtikelNr=${Number(props.catalogData.ProductId)}`;
});

const mediaDownloadFileName = computed(() => {
  if (fischer.isProductSeriesEnabled()) {
    return props.catalogData?.seriesData?.Name
      ? `${props.catalogData.seriesData.Name}-${props.catalogData.ProductName}`
      : props.catalogData?.ProductName;
  }

  return props.catalogData?.categoryData?.categoryName
    ? `${props.catalogData.categoryData.categoryName}-${props.catalogData.ProductName}`
    : props.catalogData?.ProductName;
});

const getCurrentCart = () => {
  if (fischer.cartId()) {
    try {
      getCart(false, true);
    } catch (error) {
      console.error('could not get current cart!', error);
    }
  }
};

const openMediaDownloadDialog = () => {
  mediaDownloadDialog.value?.show();
};

const openRetailSearchDialog = () => {
  // TRACKING EVENT
  if (!IS_SSR) {
    const productNameValue = `${props.catalogData?.ProductId} - ${props.catalogData?.ProductName}`;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'RCBuyLocal',
      productName: productNameValue,
    });
  }

  retailSearchDialog.value?.show();
};

const openProductRetailerDialog = () => {
  // TRACKING EVENT
  if (!IS_SSR) {
    const productNameValue = `${props.catalogData?.ProductId} - ${props.catalogData?.ProductName}`;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'RCBuyProduct',
      productName: productNameValue,
    });
  }

  productRetailerDialog.value?.show();
};

const addProduct = async () => {
  if (!props.catalogData?.ProductId) return;

  addToCartLoading.value = true;
  const res = await addProductToCart({
    productID: props.catalogData.ProductId,
    productInitialQuantity: 1,
    unit: undefined,
  });
  if (res?.success) {
    addToCartLoading.value = false;
  }
};

const onOpenCadDetailsDialog = () => {
  cadDetailsDialog.value?.open();
};

const onIntersect = (observer: any) => {
  stickyEnabled.value = !observer[0].isIntersecting && observer[0].boundingClientRect.top > 0;
};

onMounted(() => {
  layoutStore.setProductDetailHeadMounted(true);

  if (!IS_SSR && (isRetailConnectCart.value || isShop.value)) {
    getCurrentCart();
  }
});
</script>

<style scoped src="./product-detail-head-fi_product-detail-head-fite-fi" />

<style src="./product-detail-head-fi_product-detail-head-fite-fi-unscoped" />
